import { Location } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import DataSource from 'devextreme/data/data_source';
import { alert, confirm } from 'devextreme/ui/dialog';
import validationEngine from 'devextreme/ui/validation_engine';
import * as _ from 'lodash';
import { BsModalService } from 'ngx-bootstrap/modal';
import { AppInformationService, AuthenticationService, RAFUnitOfWork, UtilityService } from 'src/app/core/core.module';
import { Invoice, InvoiceLine, Job, JobStatus, TimeEntry } from 'src/app/core/data/entities';
import { ReportService } from 'src/app/core/data/report.service';
import { JobDialogComponent } from 'src/app/jobs/job-dialog/job-dialog.component';
import { DxDataGridComponent } from '../../../../node_modules/devextreme-angular';
import { EmailDialogComponent } from '../email-dialog/email-dialog.component';

@Component({
    selector: 'app-invoice-detail',
    templateUrl: './invoice-detail.component.html',
    styleUrls: ['./invoice-detail.component.scss'],
    standalone: false
})
export class InvoiceDetailComponent implements OnInit {
    invoice: any;
    lines: any;
    jobs: any;
    jobsDS: DataSource;
    selectedJob: any;
    timeEntries: any;
    materials: any;
    purchaseOrderLines: any;

    key: any;
    employee: any;
    companies: any;
    scheduleGroups: any;
    states: any;
    countries: any[] = ['US', 'OTHER'];
    timeCategories: any;
    currentCategoryIds: any;
    selectedCategoryIds: any;

    validationGroup = 'invoiceValidationGroup';
    errorAction = 'saving';

    readOnly = true;

    @ViewChild('linesGrid') linesGrid: DxDataGridComponent;
    @ViewChild('materialsGrid') materialsGrid: DxDataGridComponent;
    @ViewChild('timeGrid') timeGrid: DxDataGridComponent;
    @ViewChild('dataGrid') dataGrid: DxDataGridComponent;

    constructor(
        private uow: RAFUnitOfWork,
        public auth: AuthenticationService,
        private route: ActivatedRoute,
        protected location: Location,
        private modalService: BsModalService,
        private appInfo: AppInformationService,
        private router: Router,
        private utility: UtilityService,
        private reportService: ReportService
    ) {}

    async ngOnInit() {
        const statuses = await this.uow.lookups.statuses;
        this.appInfo.pleaseWaitShow();
        this.readOnly = !this.auth.hasRole('Admin');
        this.jobs = (await this.uow.getJobs()) as Job[];
        this.jobsDS = new DataSource({
            store: this.jobs,
            paginate: true
        });
        this.selectedJob = null;
        this.materials = [];
        this.timeEntries = [];
        this.purchaseOrderLines = [];

        this.key = this.route.snapshot.params['id'];
        await this.getData();
        this.appInfo.title = `Invoice Detail - #${this.invoice.invoiceNumber}`;
        this.appInfo.pleaseWaitHide();
    }

    async ngOnDestroy() {
        await this.uow.rollback();
    }

    async getData() {
        if (this.key === 'new') {
            this.invoice = this.uow.invoices.createEntity() as Invoice;
            this.invoice.created = new Date();
            this.invoice.createdById = this.auth.userValue.id;
            this.invoice.invoiceNumber = await this.utility.getNextInvoiceNumber();
            this.lines = [];
        } else {
            this.invoice = (await this.uow.invoices.byId(this.key, ['invoiceLines', 'job.jobMaterials', 'job.timeEntries.employee'])) as Invoice;
            this.lines = this.invoice.invoiceLines;
            if (this.invoice.job) await this.getJobData(this.invoice.jobId);
        }
        await this.calcTotal();
    }

    async getJobData(jobId: number) {
        if (this.invoice.job) this.selectedJob = await this.uow.jobs.byId(this.invoice.jobId, ['jobMaterials', 'timeEntries.employee']);
        this.selectedJob = await this.uow.jobs.byId(jobId, ['jobMaterials', 'timeEntries.employee', 'purchaseOrderLines']);
        this.purchaseOrderLines = this.selectedJob.purchaseOrderLines.filter((x) => {
            return !x.deleted;
        });
        this.materials = this.selectedJob.jobMaterials;
        this.timeEntries = this.selectedJob.timeEntries as TimeEntry[];
        this.timeEntries = this.timeEntries.map((entry) => {
            if (!entry.deleted) return entry;
        });
        this.invoice.billingAddress1 = this.selectedJob.billingAddress1;
        this.invoice.billingAddress2 = this.selectedJob.billingAddress2;
        this.invoice.billingCity = this.selectedJob.billingCity;
        this.invoice.billingState = this.selectedJob.billingState;
        this.invoice.billingZip = this.selectedJob.billingZip;
    }

    hasChanges() {
        return this.uow.hasChanges();
    }

    async jobSelectionChanged(e) {
        await this.getJobData(e.value);
    }
    selectionChange(e) {}
    onRowUpdating(e) {}

    onToolbarPreparing(e) {
        e.toolbarOptions.items.unshift(
            {
                location: 'before',
                template: 'toolbarButtons'
            },

            {
                widget: 'dxButton',
                options: {
                    icon: 'refresh',
                    elementAttr: { title: 'Reset Grid Layout' },
                    onClick: () => {
                        e.component.state({});
                    }
                },
                location: 'after'
            }
        );
    }

    print() {
        this.reportService.openInvoiceReport(this.invoice.id);
    }
    async calcTotal() {
        if (this.invoice.total !== this.invoice.linesTotal) {
            this.invoice.total = this.invoice.linesTotal;
            this.invoice.processingFee = this.invoice.linesTotal * 0.03;
            await this.uow.commit();
        }
    }

    async addLines(type?: any) {
        const line = this.uow.invoiceLines.createEntity() as InvoiceLine;
        line.invoice = this.invoice;
        line.lineNumber = this.invoice.getNextLineNumber();
        line.created = new Date();
        line.createdById = this.auth.userValue.id;
        this.lines.push(line);

        await this.calcTotal();
    }

    async removeLine() {
        const selectedRows = this.linesGrid.instance.getSelectedRowsData();
        if (selectedRows.length === 0) return;

        _.forEachRight(selectedRows, (x) => {
            x.entityAspect.setDeleted();
        });
        this.updateDisplayOrder(this.lines);

        await this.calcTotal();
    }

    async editBilling() {
        console.log(this.selectedJob);
        if (!this.selectedJob) return;
        await this.save();
        const modalRef = this.modalService.show(JobDialogComponent, {
            class: 'modal-md',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                jobId: this.selectedJob.id,
                mode: 'invoice'
            }
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                this.appInfo.pleaseWaitShow();
                await this.getData();
            }
            modalRef.hide();
            this.appInfo.pleaseWaitHide();
        });
    }
    addMiscLine() {}

    async email() {
        const modalRef = this.modalService.show(EmailDialogComponent, {
            class: 'modal-md',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                mode: 'PurchaseOrder',
                entityId: this.invoice.id,
                defaultEmailAddress: this.invoice.job.email
            }
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success && !!result.value) {
                this.appInfo.pleaseWaitShow();
                await this.utility.emailReport(result.value);
                this.appInfo.pleaseWaitHide();
                await alert(`Email Sent!`, `${this.appInfo.settings.company}`);
                this.invoice.emailed = new Date();
                await this.uow.commit();
            }
            modalRef.hide();
        });
    }

    async save() {
        if (!this.validate()) {
            return;
        }
        if (!this.invoice.jobId) {
            await alert(`Job not selected`, `${this.appInfo.settings.company}`);
            return;
        }
        if (
            this.key === 'new' &&
            this.invoice.job.statusId !== JobStatus.Statuses.Complete &&
            (await confirm('Would you like to complete this job?', `${this.appInfo.settings.company}`))
        ) {
            this.invoice.job.completed = new Date();
            this.invoice.job.completedById = this.auth.userValue.id;
            this.invoice.job.statusId = JobStatus.Statuses.Complete;
        }

        this.appInfo.pleaseWaitShow();
        await this.calcTotal();
        await this.uow.commit();
        if (this.key === 'new') {
            this.router.navigate(['../' + this.invoice.id], { relativeTo: this.route, replaceUrl: true, state: { navigateOnSaveNew: true } });
        }
        this.key = this.invoice.id;
        this.appInfo.pleaseWaitHide();
    }

    cancel() {
        this.uow.rollback();
        // this.close();
    }

    close() {
        try {
            this.location.back();
        } catch {
            this.router.navigate(['../'], { relativeTo: this.route });
        }
    }

    validate() {
        const groupConfig = validationEngine.getGroupConfig(this.validationGroup);
        if (groupConfig) {
            const result = validationEngine.validateGroup(this.validationGroup);
            if (!result.isValid) {
                let message = '';
                result.brokenRules.forEach((brokenRule) => {
                    if (brokenRule.type === 'custom') message = `${brokenRule.message}\n${message}`;
                });
                this.appInfo.notifyToast(`Please fix errors before ${this.errorAction}.\n\n${message}`, 'error');
                return false;
            }
        }
        return true;
    }

    async move(invoiceLine: InvoiceLine, direction: 'up' | 'down') {
        // if (this.processing) return;
        // this.processing = true;
        const increment = direction === 'down' ? 1 : -1;
        const otherInvoiceLine = this.lines.find((otherInvoiceLine) => otherInvoiceLine.lineNumber === invoiceLine.lineNumber + increment);
        if (otherInvoiceLine) {
            invoiceLine.lineNumber += increment;
            otherInvoiceLine.lineNumber -= increment;
            this.sort(this.lines);
            await this.save();
        }
        // this.processing = false;
    }

    sort(invoiceLines: InvoiceLine[]) {
        invoiceLines.sort((a, b) => (a.lineNumber || 9999) - (b.lineNumber || 9999));
    }

    updateDisplayOrder(invoiceLines: InvoiceLine[]) {
        _.orderBy(this.lines, 'lineNumber').forEach((invoiceLine, index) => {
            if (invoiceLine.lineNumber !== index + 1) invoiceLine.lineNumber = index + 1;
        });
    }
}
